import { useState } from 'react'

import type { SortDirection } from '@aeris/ui'

/**
 * Which column a list is sorted by, and which way.
 *
 * Six list screens each needed the same three lines of state and the same
 * "clicking a new column resets the page" rule. The rule is the reason this is
 * a hook rather than a convention: a sort change that leaves the offset alone
 * lands the user on page four of a list they just reordered, looking at rows
 * that have nothing to do with what they clicked.
 *
 * The key is passed through to the API, so the column identifiers are the
 * server's own (`due_at`, `legalName`, `total_amount`) rather than a second
 * vocabulary that has to be mapped. Two of the contracts disagree about the
 * name of the direction parameter — finance says `order`, everything else says
 * `direction` — which is a wart worth leaving visible at the call site rather
 * than papering over here.
 */
export type TableSort<K extends string> = {
  readonly sort: K
  readonly direction: SortDirection
  /**
   * Spread onto a `SortableHeaderCell`. Supplies `sorted` and `onSort`, so a
   * column cannot be wired to display one state and set another.
   */
  readonly column: (key: K) => {
    sorted: SortDirection | false
    onSort: (direction: SortDirection) => void
  }
}

export const useTableSort = <K extends string>(
  initialSort: K,
  initialDirection: SortDirection = 'asc',
  /** Called whenever the sort changes — normally to send the list back to page 1. */
  onChange?: () => void
): TableSort<K> => {
  const [sort, setSort] = useState<K>(initialSort)
  const [direction, setDirection] = useState<SortDirection>(initialDirection)

  return {
    sort,
    direction,
    column: (key) => ({
      sorted: sort === key ? direction : false,
      onSort: (next) => {
        setSort(key)
        setDirection(next)
        onChange?.()
      },
    }),
  }
}

/**
 * The sort, flattened for a react-query key.
 *
 * The key has to carry it or the cache answers a re-sorted list with the rows
 * from the previous order — the request never leaves, and the arrow flips over
 * a table that did not move.
 */
export const sortKey = <K extends string>(
  state: TableSort<K>
): { sort: K; direction: SortDirection } => ({
  sort: state.sort,
  direction: state.direction,
})
